import React, { memo } from 'react';
import { Box, createStyles, makeStyles } from '@material-ui/core';
import { useMutation } from '@apollo/client';
import { editProcedureAnesthesiaType } from '@/graph/procedures';
import PopupSelectMenu from '../shared/PopupSelectMenu';

export const anesthesiaTypes = [
  'GEN',
  'MAC',
  'SED',
  'SAB',
  'CLE',
  'ARM - C-ARM',
  'C - Choice',
  'CO - Combo',
  'CS - Conscious Sedation',
  'E - Epidural',
  'EQ - Equipment Needed',
  'IG - General Anesthesia',
  'GB - General w/Block',
  'GC - General w/C-Arm',
  'GM - General w/Marking',
  'GV - General w/Vanco',
  'IB - Interscalene Block',
  'IR - IV Regional',
  'I - IV Sedation',
  'L- Local',
  'M - MAC',
  'MCB - MAC W/Block',
  'IMSD - MAC W/SEDATION',
  'MD - MD/RN Sedation',
  'MIN - Mini C Arm',
  'IN - None',
  'OVR - Overnight',
  'PND - Pending',
  'P&A - Popliteal & Adductor',
  'PFB - Popliteal Fossa Bloc',
  'RB - Retrobulbar Block',
  'S - Spinal',
  'EAR - T PLAST',
  'TVA - TIVA',
  'T - Topical',
] as const;

export type AnesthesiaTypes = (typeof anesthesiaTypes)[number];

const formatAnesthesiaType = (value?: string) => {
  const matches = value?.match(/^(\S+)\s*-\s*.+$/);
  const group1 = matches?.[1];

  if (group1) return group1;

  const upper = (value || '').toUpperCase();
  return isAnesthesiaType(upper)
    ? value?.toUpperCase()
    : value
      ? value.split(' ').reduce((acc, curr) => acc + curr[0], '')
      : '';
};

const isAnesthesiaType = (x: string): x is AnesthesiaTypes => (anesthesiaTypes as readonly string[]).includes(x);

const AnesthesiaType = ({
  procedureId,
  editableAnesthesiologist,
  anesthesiaType,
}: {
  procedureId: number;
  editableAnesthesiologist?: boolean;
  anesthesiaType?: string;
}) => {
  const classes = useStyles();

  const [setAnesthesiaType] = useMutation(editProcedureAnesthesiaType);

  const options = React.useMemo(() => anesthesiaTypes.map(value => ({ id: value, label: value })), []);

  const handleSelect = async (value: string) => {
    await setAnesthesiaType({ variables: { id: procedureId, anesthesiaType: value } });
  };

  return (
    <PopupSelectMenu
      options={options}
      selectedId={anesthesiaType}
      editable={editableAnesthesiologist}
      onSelect={handleSelect}
      renderTrigger={({ onClick }) => (
        <Box className={classes.editLabel} onClick={onClick}>
          {formatAnesthesiaType(anesthesiaType)}
        </Box>
      )}
    />
  );
};

const useStyles = makeStyles(theme =>
  createStyles({
    editLabel: {
      fontSize: '0.857em',
      color: theme.palette.primary.main,
      marginLeft: '0.5em',
    },
  })
);

export default memo(AnesthesiaType);
